import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";

interface SchemaOrgProps {
  type: "organization" | "localBusiness" | "faq" | "service";
}

export function SchemaOrg({ type }: SchemaOrgProps) {
  const { t, i18n } = useTranslation();

  const getFAQItems = (): Array<{ question: string; answer: string }> => {
    const raw = t("faq.items", { returnObjects: true });
    if (Array.isArray(raw)) {
      return raw.filter(
        (item): item is { question: string; answer: string } =>
          typeof item === "object" && item !== null && "question" in item && "answer" in item
      );
    }
    return [];
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "M&H Global Market GmbH",
    "alternateName": ["M&H Global Market", "MH Global Market"],
    "url": "https://mhglobalmarket.de",
    "logo": "https://mhglobalmarket.de/logo.png",
    "description": t("seo.description"),
    "foundingDate": "2024",
    "founders": [
      { "@type": "Person", "name": "Marisa Granada" },
      { "@type": "Person", "name": "Hoda Fakhro" }
    ],
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Marie-Curie-Straße 1, Raum 2123",
      "addressLocality": "Oldenburg",
      "postalCode": "26129",
      "addressRegion": "Niedersachsen",
      "addressCountry": "DE"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+49-151-20309420",
      "email": "h.fakhro@mhglobalmarket.de",
      "contactType": "sales",
      "availableLanguage": ["German", "English", "Spanish", "Arabic"]
    },
    "areaServed": [
      { "@type": "Country", "name": "Germany" },
      { "@type": "Country", "name": "Austria" },
      { "@type": "Country", "name": "Switzerland" },
      { "@type": "Country", "name": "Spain" },
      { "@type": "Country", "name": "United Arab Emirates" },
      { "@type": "Country", "name": "Saudi Arabia" },
      { "@type": "Country", "name": "Mexico" },
      { "@type": "Country", "name": "Colombia" }
    ],
    "knowsAbout": [
      "International Market Entry",
      "Export Strategy",
      "Trade Negotiations",
      "Retail Listing",
      "Supply Chain Management",
      "Market Validation",
      "Market Scaling"
    ],
    "knowsLanguage": ["de", "en", "es", "ar"],
    "slogan": "Internationale Markteintritte, operativ umgesetzt."
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "M&H Global Market GmbH",
    "image": "https://mhglobalmarket.de/logo.png",
    "url": "https://mhglobalmarket.de",
    "description": t("seo.description"),
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Marie-Curie-Straße 1, Raum 2123",
      "addressLocality": "Oldenburg",
      "postalCode": "26129",
      "addressRegion": "Niedersachsen",
      "addressCountry": "DE"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 53.1435,
      "longitude": 8.2146
    },
    "telephone": "+49-151-20309420",
    "email": "h.fakhro@mhglobalmarket.de",
    "priceRange": "$$",
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "18:00"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": getFAQItems().map((item) => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "M&H Global Market GmbH",
    "description": t("seo.description"),
    "url": "https://mhglobalmarket.de",
    "serviceType": [
      "Markteintrittsberatung",
      "Market Entry Consulting",
      "Exportberatung",
      "Händlerzugang & Listung",
      "Operative Exportabteilung",
      "Marktvalidierung & Skalierung",
      "International Business Development"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Markteintrittsmodule",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Markteintritt & Entscheidungsgrundlage",
            "description": "Datenbasierte Marktanalyse mit Go-/No-Go-Empfehlung für den internationalen Markteintritt."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Marktzugang & Handel",
            "description": "Aktive Händleransprache und Verhandlungsbegleitung bis zur Listung in europäischen Handelsketten."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Operative Umsetzung & Absicherung",
            "description": "Logistikkoordination, Stammdatenmanagement und Prozesssteuerung für stabile Marktpräsenz."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Marktvalidierung & Skalierung",
            "description": "Datenbasierte Skalierungsentscheidungen auf Basis realer Abverkaufsdaten und Marktfeedback."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Vollzugang Europa (Premium)",
            "description": "All-Inclusive-Modell für den gesamten europäischen Markteintritt aus einer Hand."
          }
        }
      ]
    },
    "areaServed": [
      { "@type": "Place", "name": "Europe" },
      { "@type": "Place", "name": "Latin America" },
      { "@type": "Place", "name": "Middle East" }
    ],
    "provider": {
      "@type": "Organization",
      "name": "M&H Global Market GmbH"
    }
  };

  const schemas: Record<string, object> = {
    organization: organizationSchema,
    localBusiness: localBusinessSchema,
    faq: faqSchema,
    service: serviceSchema
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schemas[type])}
      </script>
    </Helmet>
  );
}